import { format, parseISO, isValid } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const KATHMANDU_TIMEZONE = 'Asia/Kathmandu';

/**
 * Format a date string to Kathmandu time
 * @param dateStr - ISO date string or date object
 * @param formatStr - date-fns format string (default: 'yyyy-MM-dd HH:mm:ss')
 */
export function formatInKathmanduTime(dateStr: string | Date, formatStr = 'yyyy-MM-dd HH:mm:ss'): string {
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    if (!isValid(date)) {
      return 'Invalid Date';
    }
    
    const kathmanduTime = toZonedTime(date, KATHMANDU_TIMEZONE);
    return format(kathmanduTime, formatStr);
  } catch (error) {
    console.error('Error formatting date in Kathmandu time:', error);
    return 'Invalid Date';
  }
}

/**
 * Get current date/time in Kathmandu
 */
export function getKathmanduNow(): Date {
  return new Date();
}

/**
 * Format date for display (e.g., "Today, 10:30 AM" or "Jan 15, 2:30 PM")
 */
export function formatKathmanduDateTimeDisplay(dateStr: string | Date): string {
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    if (!isValid(date)) {
      return 'Invalid Date';
    }

    const kathmanduTime = toZonedTime(date, KATHMANDU_TIMEZONE);
    const now = new Date();
    const nowInKathmandu = toZonedTime(now, KATHMANDU_TIMEZONE);
    
    const isToday = kathmanduTime.toDateString() === nowInKathmandu.toDateString();
    const tomorrow = new Date(nowInKathmandu);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = kathmanduTime.toDateString() === tomorrow.toDateString();

    const timeStr = format(kathmanduTime, 'hh:mm a');

    if (isToday) return `Today, ${timeStr}`;
    if (isTomorrow) return `Tomorrow, ${timeStr}`;

    return format(kathmanduTime, 'MMM d, yyyy, hh:mm a');
  } catch (error) {
    console.error('Error formatting date for display:', error);
    return 'Invalid Date';
  }
}

/**
 * Check if a date is within the next hour (in Kathmandu time)
 */
export function isWithinNextHour(dateStr: string | Date): boolean {
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    if (!isValid(date)) {
      return false;
    }

    const kathmanduTime = toZonedTime(date, KATHMANDU_TIMEZONE);
    const now = toZonedTime(new Date(), KATHMANDU_TIMEZONE);
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);

    const diffMs = kathmanduTime.getTime() - now.getTime();
    return diffMs > 0 && diffMs < 60 * 60 * 1000;
  } catch (error) {
    console.error('Error checking if date is within next hour:', error);
    return false;
  }
}
